/**
 * Suche ueber Ressourcen.
 *
 * Eine einzige Eingabezeile, die beides kann: `#schlagwort` filtert nach
 * Schlagwort, alles andere ist Volltext ueber Titel, Text und URL. Zwei
 * getrennte Felder waeren mehr Oberflaeche fuer weniger Nutzen - man tippt
 * ohnehin drauflos.
 *
 * Rein und ohne Dexie: die Suche laeuft in memory ueber die bereits geladenen
 * Zeilen. Bei der Groessenordnung einer persoenlichen Wissenssammlung (einige
 * tausend Eintraege) ist das deutlich schneller als eine Indexabfrage je
 * Tastendruck.
 */
import type { ID, ParaBucket, Resource, ResourceKind } from '@/domain/types';

export interface ParsedQuery {
  /** Schlagworte aus `#...`, klein geschrieben. */
  tags: string[];
  /** Der Rest, klein geschrieben. */
  text: string;
}

/** Schlagworte normalisieren: klein, ohne Raute, ohne Rand-Leerzeichen. */
export function normalizeTag(raw: string): string {
  return raw.trim().replace(/^#+/, '').toLowerCase();
}

/** Aus freier Eingabe eine Schlagwortliste machen - Komma oder Leerzeichen. */
export function parseTags(input: string): string[] {
  const roh = input.split(/[,\s]+/).map(normalizeTag).filter((t) => t.length > 0);
  return [...new Set(roh)];
}

export function parseQuery(query: string): ParsedQuery {
  const tags: string[] = [];
  const worte: string[] = [];

  for (const teil of query.trim().split(/\s+/)) {
    if (teil === '') continue;
    if (teil.startsWith('#') && teil.length > 1) tags.push(normalizeTag(teil));
    else worte.push(teil.toLowerCase());
  }

  return { tags, text: worte.join(' ') };
}

/**
 * Passt die Ressource zur Suche?
 *
 * Schlagworte werden UND-verknuepft (`#studium #klausur` heisst beides), der
 * Text muss als Ganzes irgendwo vorkommen. Das entspricht dem, was man beim
 * Tippen erwartet: jedes weitere Wort grenzt weiter ein.
 */
export function matchesQuery(resource: Resource, parsed: ParsedQuery): boolean {
  for (const tag of parsed.tags) {
    if (!resource.tags.some((t) => normalizeTag(t) === tag)) return false;
  }

  if (parsed.text === '') return true;

  const heuhaufen = [
    resource.title,
    resource.body ?? '',
    resource.url ?? '',
    resource.tags.join(' '),
  ].join(' ').toLowerCase();

  return heuhaufen.includes(parsed.text);
}

export interface ResourceFilter {
  query?: string;
  bucket?: ParaBucket;
  kind?: ResourceKind;
  /** Leer heisst "alle Bereiche". */
  activeAreaIds?: ID[];
  /** Nur ungelesene Lesezeichen. */
  unreadOnly?: boolean;
}

export function filterResources(
  resources: Resource[],
  filter: ResourceFilter,
): Resource[] {
  const parsed = parseQuery(filter.query ?? '');

  return resources.filter((r) => {
    if (filter.bucket && r.bucket !== filter.bucket) return false;
    if (filter.kind && r.kind !== filter.kind) return false;
    if (filter.unreadOnly && r.read) return false;
    if (filter.activeAreaIds?.length
      && (r.areaId === null || !filter.activeAreaIds.includes(r.areaId))) return false;
    return matchesQuery(r, parsed);
  });
}

/** Alle vergebenen Schlagworte mit Haeufigkeit, haeufigste zuerst. */
export function collectTags(resources: Resource[]): { tag: string; count: number }[] {
  const zaehler = new Map<string, number>();

  for (const r of resources) {
    for (const roh of r.tags) {
      const tag = normalizeTag(roh);
      if (tag === '') continue;
      zaehler.set(tag, (zaehler.get(tag) ?? 0) + 1);
    }
  }

  return [...zaehler.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag, 'de'));
}

/** Angepinnt zuerst, dann zuletzt geaendert. */
export function sortResources(resources: Resource[]): Resource[] {
  return [...resources].sort(
    (a, b) => b.pinned - a.pinned || b.updatedAt.localeCompare(a.updatedAt),
  );
}
